import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Gift, X, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (amount, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: (currency || "USD").toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).format(Number(amount || 0));
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`;
  }
};

const REASON_MESSAGES = {
  not_found: "Gift card not found",
  wrong_org: "Gift card not found",
  org_not_found: "Organization not found",
  expired: "This gift card has expired",
  disabled: "This gift card is no longer active",
  depleted: "This gift card has no balance remaining",
  currency_mismatch: "This gift card is in a different currency",
  unsupported_currency: "Currency not supported",
  card_temporarily_locked: "This gift card is being used elsewhere — try again in a moment",
  booking_too_small_for_giftcard: "Booking is too small to apply a gift card",
  booking_below_stripe_minimum: "Booking is too small for our payment processor",
  non_positive_amount: "Invalid amount",
  too_many_attempts: "Too many attempts — please wait a few minutes and try again",
};

/**
 * Reusable gift card apply block — calls the public hold endpoint and lifts
 * the resulting hold info back to the parent via onApplied. Parent is
 * responsible for releasing the hold (DELETE) on cancel / unmount.
 */
export default function GiftCardApplyBlock({
  apiUrl,              // e.g. process.env.REACT_APP_BACKEND_URL
  orgSlug,             // organization slug
  bookingTotal,        // major-unit total of the booking
  currency,            // e.g. "USD"
  customerEmail,       // optional
  applied,             // { code, holdId, applied, stripeCharge, currency, expiresAt } | null
  onApplied,           // (holdInfo) => void
  onRemoved,           // (holdId) => Promise<void>
  defaultOpen = false, // whether to start expanded
}) {
  const [open, setOpen] = useState(defaultOpen || !!applied);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleApply = async () => {
    // Sanitize: strip all whitespace inside (operators often type 'ABCD 1234' from emails)
    const trimmed = code.replace(/\s+/g, "").toUpperCase();
    if (!trimmed) { setError("Please enter a code"); return; }
    if (trimmed.length < 4) { setError("Code is too short"); return; }
    setSubmitting(true);
    setError("");
    try {
      const resp = await fetch(`${apiUrl}/api/v2/public/gift-cards/hold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: trimmed,
          org_slug: orgSlug,
          requested_amount: bookingTotal,
          booking_total: bookingTotal,
          currency: (currency || "USD").toUpperCase(),
          customer_email: customerEmail || null,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        onApplied?.({
          code: trimmed,
          holdId: data.hold_id,
          applied: data.applied_amount,
          stripeCharge: data.stripe_charge_amount,
          currency: data.card_currency,
          expiresAt: data.expires_at,
        });
        toast.success("Gift card applied");
        setCode("");
      } else {
        const body = await resp.json().catch(() => ({}));
        const reason = body?.detail || "invalid";
        setError(REASON_MESSAGES[reason] || "Could not apply gift card");
      }
    } catch (e) {
      setError("Network error — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!applied) return;
    const holdId = applied.holdId;
    onRemoved?.(holdId);
    // Fire-and-forget hold release
    fetch(`${apiUrl}/api/v2/public/gift-cards/hold/${encodeURIComponent(holdId)}`, { method: "DELETE" }).catch(() => {});
  };

  if (applied) {
    return (
      <div
        className="flex items-center justify-between gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg"
        data-testid="wizard-giftcard-applied"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Gift className="w-4 h-4 text-indigo-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-mono font-semibold text-indigo-900 truncate">{applied.code}</div>
            <div className="text-xs text-indigo-600">{fmtMoney(applied.applied, applied.currency)} applied</div>
          </div>
        </div>
        <button
          onClick={handleRemove}
          className="text-gray-400 hover:text-gray-700 p-1"
          title="Remove gift card"
          data-testid="wizard-giftcard-remove-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
        data-testid="wizard-giftcard-toggle"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Gift className="w-4 h-4 text-indigo-500" />
          Have a gift card?
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-2">
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\s+/g, "").toUpperCase()); setError(""); }}
              placeholder="Enter gift card code"
              maxLength={32}
              autoComplete="off"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleApply(); } }}
              disabled={submitting}
              data-testid="wizard-giftcard-input"
              className="flex-1 font-mono text-sm h-10"
            />
            <Button
              type="button"
              onClick={handleApply}
              disabled={submitting || !code.trim()}
              data-testid="wizard-giftcard-apply-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white h-10"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
            </Button>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
